import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import Breadcrumbs from '../../../coreComponents/breadcrumbs';

import CommonCardHeader from '../../../coreComponents/CommonCardHeader';

import AjaxSourcedData from '../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { getCoursesListTableColumns } from '../../../data/formsAndTable/tables/dataTables/DataSource';
import {
  useCreateCourseMutation,
  useDeleteCourseMutation,
  useGetAllCoursesQuery,
  useUpdateCourseMutation,
} from '../../../reduxToolkit/reducers/usersSlice';
import { useEffect, useState } from 'react';
import { CourseData, CourseFormData } from '../../../types/Tables';
import SimpleModal from '../../uiKits/modal/basicModal/SimpleModal';
import ValidationForm from '../../formsAndTable/forms/formControls/formValidation/validationForm';
import { AddCourseInput, CalendarItem, EditCourseInput } from '../../../types/Forms';
import Confirmation from '../../../coreComponents/Confirmation';
import { CourseStateStatus } from '../../../types/Courses';
import { useTranslation } from 'react-i18next';
import { enumToSelectOptions } from '../../../utils';
import { getEditCourseData } from '../../../services/api';
import Swal from 'sweetalert2';
import { C } from '@fullcalendar/core/internal-common';
import { Primary } from '../../../constants';
import Loader from '../../../layout/Loader';
import Error404 from '../../pages/others/errorPage/Error404';
import { NoDataComponent } from '../../formsAndTable/forms/formControls/NoDataComponent';
import { baseLang } from '../../../lib/utils';
const formDefaultEdit: Partial<CourseFormData> = {
  name: '',
  name_uk: '',
  name_ru: '',
  description: '',
  description_uk: '',
  description_ru: '',
  categories: [],
};

const formDefaultCreate: Partial<CourseFormData> = {
  name: '',
  name_uk: '',
  name_ru: '',
  description: '',
  description_uk: '',
  description_ru: '',
  categories: [],
  createdAt: '',
};

const CoursesListPageContainer = () => {
  const { t, i18n } = useTranslation();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(0);
  const [pendingDeleteCourse, setPendingDeleteCourse] = useState<CourseData | null>(null);
  const [confirmation, setConfirmation] = useState<number | null>(null);
  const [formDefaultsCreate, setFormDefaultsCreate] =
    useState<Partial<CourseFormData>>(formDefaultCreate);
  const [formDefaultsEdit, setFormDefaultsEdit] =
    useState<Partial<CourseFormData>>(formDefaultEdit);
  const [modalAction, setModalAction] = useState({
    id: 1,
    action: 'create',
  });
  const { data, isSuccess, isError, isLoading, isFetching } = useGetAllCoursesQuery({});
  const [updateCourse] = useUpdateCourseMutation();
  const [deleteCourse] = useDeleteCourseMutation();
  const [createCourse] = useCreateCourseMutation();
  const pageLang = baseLang(i18n.language);
  useEffect(() => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    const dateString = `${year}-${month}-${day}`;
    setFormDefaultsCreate({
      ...formDefaultCreate,
      createdAt: dateString,
    });
  }, []);

  const handleEdit = async (course: CourseData) => {
    setModalAction({
      id: 2,
      action: 'edit',
    });
    setCurrentCourse(course.id);
    try {
      const translateUk = {
        id: 0,
        lang: 'uk',
        name: '',
        description: '',
      };

      const translateRu = {
        id: 1,
        lang: 'ru',
        name: '',
        description: '',
      };
      if (course.translations) {
        for (const tr of course.translations) {
          if (tr.lang === 'uk') {
            translateUk.id = tr.id ?? 0;
            translateUk.name = tr.name ?? '';
            translateUk.description = tr.description ?? '';
          } else if (tr.lang === 'ru') {
            translateRu.id = tr.id ?? 1;
            translateRu.name = tr.name ?? '';
            translateRu.description = tr.description ?? '';
          }
        }
      }

      setFormDefaultsEdit({
        name: course.name,
        name_uk: translateUk.name,
        name_ru: translateRu.name,
        description: course.description ?? '',
        description_uk: translateUk.description,
        description_ru: translateRu.description,
        createdAt: course.createdAt ? course.createdAt.split('T')[0] : undefined,
        courseStateStatus: course.courseStateStatus,
        categories:
          course.categories?.map((cat: any) => {
            const nameUk = cat.translations?.find((t: any) => t.lang === 'uk')?.name || '';
            const nameRu = cat.translations?.find((t: any) => t.lang === 'ru')?.name || '';

            return {
              id: cat.id,
              courseId: cat.courseId,
              name: cat.name,
              name_uk: nameUk,
              name_ru: nameRu,
              lessons: cat.lessons,
              category_pos: cat.category_pos ?? '',
            };
          }) ?? [],
      });
    } catch (error) {
      console.log('error', error);
    }

    setModalIsOpen(true);
    return true;
  };

  const handleDeleteCourse = (course: CourseData) => {
    setPendingDeleteCourse(course);
    setConfirmation(7);
    setShowConfirm(true);
  };
  const confirmDeletCourse = async () => {
    if (!pendingDeleteCourse) return;

    try {
      await deleteCourse(pendingDeleteCourse).unwrap();
      setConfirmation(8); // 3 = deletion success message
    } catch (error) {
      console.error('Delete error:', error);
      setConfirmation(2); // 2 = error
    } finally {
      setPendingDeleteCourse(null);
      setShowConfirm(true);
    }
  };
  const courseEditInputs: EditCourseInput[] = [
    {
      slug: 'name',
      label: t('courses.coursesList.inputs.labelCourseTitle'),
      title: t('courses.coursesList.inputs.titleCourseTitle'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseTitle'),
      type: 'text',
    },
    {
      slug: 'name_uk',
      label: t('courses.coursesList.inputs.labelCourseTitle_uk'),
      title: t('courses.coursesList.inputs.titleCourseTitle_uk'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseTitle_uk'),
      type: 'text',
    },
    {
      slug: 'name_ru',
      label: t('courses.coursesList.inputs.labelCourseTitle_ru'),
      title: t('courses.coursesList.inputs.titleCourseTitle_ru'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseTitle_ru'),
      type: 'text',
    },
    {
      slug: 'description',
      label: t('courses.coursesList.inputs.labelCourseDescription'),
      title: t('courses.coursesList.inputs.titleCourseDescription'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseDescription'),
      type: 'text',
    },
    {
      slug: 'description_uk',
      label: t('courses.coursesList.inputs.labelCourseDescription_uk'),
      title: t('courses.coursesList.inputs.titleCourseDescription_uk'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseDescription_uk'),
      type: 'text',
    },
    {
      slug: 'description_ru',
      label: t('courses.coursesList.inputs.labelCourseDescription_ru'),
      title: t('courses.coursesList.inputs.titleCourseDescription_ru'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseDescription_ru'),
      type: 'text',
    },

    {
      slug: 'createdAt',
      label: t('courses.coursesList.inputs.labelCourseCreateAt'),
      title: t('courses.coursesList.inputs.titleCourseCreateAt'),
      type: CalendarItem.Date,
      disabled: true,
    },
    {
      slug: 'courseImage',
      label: t('courses.coursesList.inputs.labelCourseImage'),
      title: t('courses.coursesList.inputs.titleCoursImage'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseImage'),
      type: 'file',
    },
    {
      slug: 'courseStateStatus',
      label: t('courses.coursesList.inputs.labelCourseStatus'),
      title: t('courses.coursesList.inputs.titleCoursStatus'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseStatus'),
      type: 'select',
      options: enumToSelectOptions(CourseStateStatus, 'courses.coursesStatus', t),
    },
  ];
  const courseCreateInputs: EditCourseInput[] = [
    {
      slug: 'name',
      label: t('courses.coursesList.inputs.labelCourseTitle'),
      title: t('courses.coursesList.inputs.titleCourseTitle'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseTitle'),
      type: 'text',
    },
    {
      slug: 'name_uk',
      label: t('courses.coursesList.inputs.labelCourseTitle_uk'),
      title: t('courses.coursesList.inputs.titleCourseTitle_uk'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseTitle_uk'),
      type: 'text',
    },
    {
      slug: 'name_ru',
      label: t('courses.coursesList.inputs.labelCourseTitle_ru'),
      title: t('courses.coursesList.inputs.titleCourseTitle_ru'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseTitle_ru'),
      type: 'text',
    },
    {
      slug: 'description',
      label: t('courses.coursesList.inputs.labelCourseDescription'),
      title: t('courses.coursesList.inputs.titleCourseDescription'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseDescription'),
      type: 'text',
    },
    {
      slug: 'description_uk',
      label: t('courses.coursesList.inputs.labelCourseDescription_uk'),
      title: t('courses.coursesList.inputs.titleCourseDescription_uk'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseDescription_uk'),
      type: 'text',
    },
    {
      slug: 'description_ru',
      label: t('courses.coursesList.inputs.labelCourseDescription_ru'),
      title: t('courses.coursesList.inputs.titleCourseDescription_ru'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseDescription_ru'),
      type: 'text',
    },
    {
      slug: 'createdAt',
      label: t('courses.coursesList.inputs.labelCourseCreateAt'),
      title: t('courses.coursesList.inputs.titleCourseCreateAt'),
      type: CalendarItem.Date,
      disabled: true,
    },
    {
      slug: 'courseImage',
      label: t('courses.coursesList.inputs.labelCourseImage'),
      title: t('courses.coursesList.inputs.titleCoursImage'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseImage'),
      type: 'file',
    },
    {
      slug: 'courseStateStatus',
      label: t('courses.coursesList.inputs.labelCourseStatus'),
      title: t('courses.coursesList.inputs.titleCoursStatus'),
      placeholder: t('courses.coursesList.inputs.placeholderCourseStatus'),
      type: 'select',
      options: enumToSelectOptions(CourseStateStatus, 'courses.coursesStatus', t),
    },
  ];
  const editCourseSubmitHandler = async (data: CourseFormData): Promise<void> => {
    try {
      const formData = new FormData();

      // Append basic fields
      formData.append('id', String(currentCourse));
      formData.append('name', data.name);
      if (data?.name_uk) {
        formData.append('name_uk', data?.name_uk);
      }
      if (data?.name_ru) {
        formData.append('name_ru', data?.name_ru);
      }

      formData.append('description', data.description || '');
      formData.append('description_uk', data.description_uk || '');

      formData.append('description_ru', data.description_ru || '');

      formData.append('createdAt', data.createdAt || new Date().toISOString());
      formData.append('courseStateStatus', data.courseStateStatus);

      // Append categories
      const categoriesPayload = (data.categories || []).map((cat) => {
        const pos = Number(cat.category_pos);
        if (!Number.isInteger(pos) || pos <= 0) {
          throw new Error('Each category must have a positive integer position');
        }
        return {
          ...(cat.id ? { id: cat.id } : {}),
          name: (cat.name ?? '').trim(),
          name_uk: (cat.name_uk ?? '').trim(),
          name_ru: (cat.name_ru ?? '').trim(),
          category_pos: pos,
          translations: [
            { lang: 'en', name: cat.name },
            { lang: 'uk', name: cat.name_uk },
            { lang: 'ru', name: cat.name_ru },
          ],
        };
      });

      formData.append('categories', JSON.stringify(categoriesPayload));
      // Append course image if present
      const file = data.courseImage;
      if (file) {
        formData.append('courseImage', file);
      }

      const res = await updateCourse(formData);

      if (res.error) {
        Swal.fire({
          title: 'Update error',
          text: 'Something went wrong!',
          icon: 'error',
          confirmButtonColor: Primary,
        });
      } else {
        Swal.fire({
          title: 'Updated!',
          text: 'Course updated!',
          icon: 'success',
          confirmButtonColor: Primary,
        });
      }
    } catch (error) {
      console.log('editCourse error', error);
      Swal.fire({
        title: 'Update error',
        text: 'Something went wrong!',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    } finally {
      setModalIsOpen(false);
      setCurrentCourse(0);
      setFormDefaultsEdit(formDefaultEdit);
    }
  };
  const newCourseSubmitHandler = async (course: CourseFormData) => {
    try {
      const formData = new FormData();

      // Append basic fields
      formData.append('id', String(currentCourse));
      formData.append('name', course.name);
      if (course?.name_uk) {
        formData.append('name_uk', course?.name_uk);
      }
      if (course?.name_ru) {
        formData.append('name_ru', course?.name_ru);
      }
      formData.append('description', course.description || '');
      if (course?.description_uk) {
        formData.append('description_uk', course?.description_uk);
      }
      if (course?.description_ru) {
        formData.append('description_ru', course?.description_ru);
      }
      formData.append('createdAt', course.createdAt || new Date().toISOString());
      // Append categories
      const categoriesWithTranslations = (course.categories || []).map((cat) => ({
        translations: [
          { lang: 'en', name: cat.name },
          { lang: 'uk', name: cat.name_uk },
          { lang: 'ru', name: cat.name_ru },
        ],
        ...(cat.category_pos !== '' && cat.category_pos !== undefined
          ? { category_pos: Number(cat.category_pos) }
          : {}),
      }));

      formData.append('categories', JSON.stringify(categoriesWithTranslations));
      formData.append('courseStateStatus', CourseStateStatus.PENDING);
      // Append course image if present
      const file = course.courseImage;

      if (file) {
        formData.append('courseImage', file);
      }
      const res = await createCourse(formData);
      setConfirmation(9);
    } catch (error) {
      console.log('Create new course error', error);
      setConfirmation(2);
    } finally {
      setModalIsOpen(false);
      setCurrentCourse(0);
      setFormDefaultsCreate(formDefaultCreate);
      setShowConfirm(true);
    }
  };
  const modalContent =
    modalAction.id === 1 ? (
      <ValidationForm<CourseFormData>
        inputs={courseCreateInputs}
        formTitle={t('courses.coursesList.createCourseFormTitle')}
        formSubTitle={t('courses.coursesList.createCourseFormSubtitle')}
        submitHandler={newCourseSubmitHandler}
        defaultValues={formDefaultsCreate}
        mode="create"
        subject="course"
      />
    ) : (
      <ValidationForm<CourseFormData>
        inputs={courseEditInputs}
        formTitle={t('courses.coursesList.editCourseFormTitle')}
        formSubTitle={t('courses.coursesList.editCourseFormSubtitle')}
        submitHandler={editCourseSubmitHandler}
        defaultValues={formDefaultsEdit}
        mode="edit"
        subject="course"
      />
    );

  const handleCreateCourse = () => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    const dateString = `${year}-${month}-${day}`;
    setFormDefaultsCreate({
      ...formDefaultsCreate,
      createdAt: dateString,
      courseStateStatus: CourseStateStatus.PENDING,
    });
    setModalAction({
      id: 1,
      action: 'create',
    });
    setModalIsOpen(true);
  };

  if (isError) return <Error404 />;
  return (
    <>
      <Breadcrumbs
        mainTitle={t('courses.coursesList.coursesListPageTitle')}
        // parent={t('courses.generalTitle')}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CommonCardHeader title={t('courses.coursesList.coursesListCardTitle')} tag />
              <CardBody>
                <Row className="mb-3">
                  <Col>
                    {' '}
                    <p>{t('courses.coursesList.coursesListPageParagraph')}</p>
                  </Col>
                  <Col className="d-flex justify-content-end">
                    {' '}
                    <div className="pr-4">
                      {' '}
                      <Button color="primary" outline onClick={handleCreateCourse} className="me-4">
                        {t('courses.coursesList.createCourseBtn')}
                      </Button>
                    </div>
                  </Col>
                </Row>

                <AjaxSourcedData
                  data={data}
                  columns={getCoursesListTableColumns(handleEdit, handleDeleteCourse, t, pageLang)}
                  noDataComponent={<NoDataComponent />}
                  loading={isLoading || isFetching}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <SimpleModal isOpen={modalIsOpen} close={() => setModalIsOpen(false)} size="xl">
        {modalContent}
      </SimpleModal>
      <SimpleModal isOpen={showConfirm} close={() => setShowConfirm(false)}>
        <Confirmation
          keyValue={confirmation}
          applyHandler={confirmDeletCourse}
          cancelHandler={() => {
            setShowConfirm(false);
            setPendingDeleteCourse(null);
            setConfirmation(null);
          }}
          close={() => setShowConfirm(false)}
        />
      </SimpleModal>
    </>
  );
};
export default CoursesListPageContainer;
